import React, { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import * as S from './styles';
import Card, { Props } from './card';

const OBSERVER_OPTIONS = { threshold: 0, rootMargin: '450px' } as const;
const SCROLL_CHECK_THROTTLE_MS = 120;

const CardContent = (props: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [elementHeight, setElementHeight] = useState<number | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const hasCheckedInitialRef = useRef(false);
  const scrollCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkVisibility = useCallback(() => {
    if (!containerRef.current) return false;
    const rect = containerRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.top < windowHeight + 150 && rect.bottom > -150;
  }, []);

  const tryShowFromScroll = useCallback(() => {
    if (isVisible) return;
    if (checkVisibility()) setIsVisible(true);
  }, [isVisible, checkVisibility]);

  const handleScrollResize = useCallback(() => {
    if (scrollCheckTimeoutRef.current != null) return;
    scrollCheckTimeoutRef.current = setTimeout(() => {
      scrollCheckTimeoutRef.current = null;
      tryShowFromScroll();
    }, SCROLL_CHECK_THROTTLE_MS);
  }, [tryShowFromScroll]);

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry?.isIntersecting != null) setIsVisible(entry.isIntersecting);
  }, []);

  useEffect(() => {
    if (!isVisible || elementHeight != null) return;
    const el = contentRef.current;
    if (!el) return;
    const height = el.offsetHeight;
    if (height > 0) setElementHeight(height);
  }, [isVisible, elementHeight]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (!hasCheckedInitialRef.current) {
      hasCheckedInitialRef.current = true;
      if (checkVisibility()) setIsVisible(true);
    }

    const prev = observerRef.current;
    if (prev) prev.disconnect();

    const observer = new IntersectionObserver(handleIntersection, OBSERVER_OPTIONS);
    observerRef.current = observer;
    observer.observe(container);

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [handleIntersection, checkVisibility]);

  useEffect(() => {
    window.addEventListener('scroll', handleScrollResize, { passive: true });
    window.addEventListener('resize', handleScrollResize);
    return () => {
      window.removeEventListener('scroll', handleScrollResize);
      window.removeEventListener('resize', handleScrollResize);
      if (scrollCheckTimeoutRef.current != null) {
        clearTimeout(scrollCheckTimeoutRef.current);
        scrollCheckTimeoutRef.current = null;
      }
    };
  }, [handleScrollResize]);

  const placeholderStyle = useMemo(
    () => ({ height: (elementHeight ?? 'auto') as number | 'auto' }),
    [elementHeight]
  );

  return (
    <S.CardContainer ref={containerRef} id={`client-${props.client.COD_CLIENTE}`}>
      {isVisible ? (
        <S.ContentWrapper ref={contentRef}>
          <Card {...props} />
        </S.ContentWrapper>
      ) : (
        <S.Placeholder style={placeholderStyle} />
      )}
    </S.CardContainer>
  );
};

export default React.memo(CardContent);
